import { supabase } from './supabaseService';
import { versionManager } from '../utils/versionManager';
import { logger } from '../utils/logger';

type AuthEventType =
  | 'login'
  | 'logout'
  | 'session_reload'
  | 'token_refresh'
  | 'role_sync'
  | 'session_change_detected'
  | 'integrity_violation'
  | 'deployment_detected'
  | 'session_validation'
  | 'forced_logout';

type LogSeverity = 'info' | 'warning' | 'error' | 'critical';

interface AuthLogEntry {
  userId: string;
  userEmail: string;
  eventType: AuthEventType;
  previousUserId?: string;
  newUserId?: string;
  previousRole?: string;
  newRole?: string;
  context?: string;
  metadata?: Record<string, any>;
  severity?: LogSeverity;
}

interface GrafanaLogEntry {
  timestamp: string;
  level: string;
  userId: string;
  userEmail: string;
  eventType: string;
  appVersion: string;
  message: string;
  metadata: Record<string, any>;
}

class StructuredLogger {
  private logQueue: AuthLogEntry[] = [];
  private isProcessing: boolean = false;
  private readonly BATCH_SIZE = 10;
  private readonly FLUSH_INTERVAL = 5000; // 5 seconds
  private flushTimer: number | null = null;
  // Set to true the first time log_auth_event returns "function not found"
  // (PGRST202). Prevents flooding the console on every login/reload in
  // environments where the audit RPC hasn't been deployed.
  private rpcMissing: boolean = false;

  constructor() {
    if (typeof window !== 'undefined') {
      this.startAutoFlush();

      window.addEventListener('beforeunload', () => {
        this.flush();
      });
    }
  }

  async logAuthEvent(entry: AuthLogEntry): Promise<void> {
    const appVersion = versionManager.getCurrentVersion();

    try {
      // Log to console immediately (sanitized in production)
      const severity = entry.severity || 'info';
      const isDevelopment = import.meta.env.DEV;

      // Only log in development or for critical/error events
      if (isDevelopment || severity === 'critical' || severity === 'error') {
        const logMessage = `[AUTH ${severity.toUpperCase()}]`;
        const logData = {
          context: entry.context,
          eventType: entry.eventType
        };

        // Use explicit console methods to avoid minification issues
        if (severity === 'critical' || severity === 'error') {
          console.error(logMessage, entry.eventType, logData);
        } else if (severity === 'warning') {
          console.warn(logMessage, entry.eventType, logData);
        } else {
          console.log(logMessage, entry.eventType, logData);
        }
      }

      // Insert directly to Supabase (don't queue for auth events)
      if (!this.rpcMissing) {
        const { error } = await supabase.rpc('log_auth_event', {
          p_user_id: entry.userId || null,
          p_user_email: entry.userEmail,
          p_event_type: entry.eventType,
          p_previous_user_id: entry.previousUserId || null,
          p_new_user_id: entry.newUserId || null,
          p_previous_role: entry.previousRole || null,
          p_new_role: entry.newRole || null,
          p_app_version: appVersion,
          p_context: entry.context || null,
          p_metadata: entry.metadata || {},
          p_severity: severity
        });

        if (error) {
          // PGRST202 = "Could not find the function public.log_auth_event".
          // 42883 = "function does not exist". Both mean the RPC is missing;
          // stop trying and silence the noise.
          if (error.code === 'PGRST202' || error.code === '42883') {
            this.rpcMissing = true;
            logger.warn('log_auth_event RPC no disponible, deshabilitando auditoría remota');
          } else {
            logger.error('Error logging to audit table', error);
          }
        }
      }

      // Also send to Grafana if configured
      await this.sendToGrafana(entry, appVersion);
    } catch (error) {
      logger.error('Error in logAuthEvent', error);
    }
  }

  private async sendToGrafana(entry: AuthLogEntry, appVersion: string): Promise<void> {
    try {
      const grafanaEnabled = localStorage.getItem('crm_grafana_enabled') === 'true';
      if (!grafanaEnabled) return;

      const grafanaLogEntry: GrafanaLogEntry = {
        timestamp: new Date().toISOString(),
        level: entry.severity || 'info',
        userId: entry.userId,
        userEmail: entry.userEmail,
        eventType: entry.eventType,
        appVersion,
        message: `Auth event: ${entry.eventType} for user ${entry.userEmail}`,
        metadata: {
          ...entry.metadata,
          context: entry.context,
          previousUserId: entry.previousUserId,
          newUserId: entry.newUserId,
          previousRole: entry.previousRole,
          newRole: entry.newRole
        }
      };

      const response = await supabase.functions.invoke('send-metrics-to-grafana', {
        body: {
          type: 'log',
          entry: grafanaLogEntry
        }
      });

      if (response.error) {
        console.warn('⚠️ Error sending log to Grafana:', response.error);
      }
    } catch (error) {
      console.warn('⚠️ Failed to send to Grafana:', error);
    }
  }

  async logLogin(userId: string, userEmail: string, role: string): Promise<void> {
    await this.logAuthEvent({
      userId,
      userEmail,
      eventType: 'login',
      newRole: role,
      context: 'user_initiated',
      severity: 'info',
      metadata: {
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      }
    });
  }

  async logLogout(userId: string, userEmail: string): Promise<void> {
    await this.logAuthEvent({
      userId,
      userEmail,
      eventType: 'logout',
      context: 'user_initiated',
      severity: 'info'
    });
  }

  async logSessionReload(userId: string, userEmail: string, role: string): Promise<void> {
    await this.logAuthEvent({
      userId,
      userEmail,
      eventType: 'session_reload',
      newRole: role,
      context: 'page_reload',
      severity: 'info'
    });
  }

  async logIntegrityViolation(
    expectedUserId: string,
    expectedEmail: string,
    receivedUserId: string,
    receivedEmail: string,
    reason: string
  ): Promise<void> {
    await this.logAuthEvent({
      userId: expectedUserId,
      userEmail: expectedEmail,
      eventType: 'integrity_violation',
      previousUserId: expectedUserId,
      newUserId: receivedUserId,
      context: 'session_validation',
      severity: 'critical',
      metadata: {
        reason,
        expectedEmail,
        receivedEmail,
        detectedAt: new Date().toISOString()
      }
    });
  }

  async logSessionChange(
    previousUserId: string,
    previousEmail: string,
    newUserId: string,
    newEmail: string,
    context: string
  ): Promise<void> {
    await this.logAuthEvent({
      userId: previousUserId,
      userEmail: previousEmail,
      eventType: 'session_change_detected',
      previousUserId,
      newUserId,
      context,
      severity: 'critical',
      metadata: {
        previousEmail,
        newEmail,
        detectedAt: new Date().toISOString(),
        userAgent: navigator.userAgent
      }
    });
  }

  async logRoleSync(
    userId: string,
    userEmail: string,
    previousRole: string,
    newRole: string,
    context: string
  ): Promise<void> {
    await this.logAuthEvent({
      userId,
      userEmail,
      eventType: 'role_sync',
      previousRole,
      newRole,
      context,
      severity: previousRole !== newRole ? 'warning' : 'info'
    });
  }

  async logDeploymentDetected(
    userId: string,
    userEmail: string,
    previousVersion: string,
    newVersion: string
  ): Promise<void> {
    await this.logAuthEvent({
      userId,
      userEmail,
      eventType: 'deployment_detected',
      context: 'version_change',
      severity: 'info',
      metadata: {
        previousVersion,
        newVersion,
        detectedAt: new Date().toISOString()
      }
    });
  }

  async getRecentLogs(userId: string, limit: number = 50): Promise<any[]> {
    try {
      const { data, error } = await supabase.rpc('get_user_audit_logs', {
        p_user_id: userId,
        p_limit: limit
      });

      if (error) {
        logger.error('Error fetching audit logs', error);
        return [];
      }

      return data || [];
    } catch (error) {
      logger.error('Error in getRecentLogs', error);
      return [];
    }
  }

  async detectAnomalies(lookbackHours: number = 24): Promise<any[]> {
    try {
      const { data, error } = await supabase.rpc('detect_session_anomalies', {
        p_lookback_hours: lookbackHours
      });

      if (error) {
        logger.error('Error detecting anomalies', error);
        return [];
      }

      return data || [];
    } catch (error) {
      logger.error('Error in detectAnomalies', error);
      return [];
    }
  }

  async getCriticalEvents(limit: number = 100): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('auth_critical_events')
        .select('*')
        .order('logged_at', { ascending: false })
        .limit(limit);

      if (error) {
        logger.error('Error fetching critical events', error);
        return [];
      }

      return data || [];
    } catch (error) {
      logger.error('Error in getCriticalEvents', error);
      return [];
    }
  }

  private startAutoFlush(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    this.flushTimer = window.setInterval(() => {
      if (this.logQueue.length > 0) {
        this.flush();
      }
    }, this.FLUSH_INTERVAL);
  }

  private async flush(): Promise<void> {
    if (this.isProcessing || this.logQueue.length === 0) return;

    this.isProcessing = true;
    const batch = this.logQueue.splice(0, this.BATCH_SIZE);

    try {
      for (const entry of batch) {
        await this.logAuthEvent(entry);
      }
    } catch (error) {
      logger.error('Error flushing log queue', error);
    } finally {
      this.isProcessing = false;
    }
  }

  // General logging methods (non-auth)
  info(message: string, metadata?: Record<string, any>): void {
    console.log(`ℹ️ ${message}`, metadata || '');
  }

  warn(message: string, metadata?: Record<string, any>): void {
    console.warn(`⚠️ ${message}`, metadata || '');
  }

  error(message: string, metadata?: Record<string, any>): void {
    logger.error(`${message}`, metadata || '');
  }
}

export const structuredLogger = new StructuredLogger();
export default structuredLogger;
